(() => {
  'use strict';

  const CART_ELEMENT_SELECTORS = 'cart-notification, cart-drawer, cart-items, cart-drawer-items';
  const STATIC_SECTION_CANDIDATES = [
    'cart-notification-product',
    'cart-notification-button',
    'cart-drawer',
    'cart-icon-bubble',
    'cart-live-region-text',
    'main-cart-items',
    'main-cart-footer'
  ];

  function getShopRoot() {
    const raw = globalThis.Shopify?.routes?.root || '/';
    return raw.endsWith('/') ? raw : `${raw}/`;
  }

  function shopPath(path) {
    return `${getShopRoot()}${String(path).replace(/^\//, '')}`;
  }

  function uniq(values) {
    return [...new Set((values || []).filter(Boolean))];
  }

  function discoverMountedSectionIds(doc = globalThis.document) {
    if (!doc?.querySelectorAll) return [];
    const ids = [];
    doc.querySelectorAll('[id^="shopify-section-"]').forEach((el) => {
      const id = String(el.id || '').replace(/^shopify-section-/, '');
      if (/cart|drawer/i.test(id)) ids.push(id);
    });
    return uniq(ids);
  }

  // Exact Ride/Dawn contract: mounted cart custom elements declare the section IDs
  // they need through getSectionsToRender(). We discover these at runtime instead
  // of freezing theme-version-specific IDs into business logic.
  function discoverNativeCartSectionIds(doc = globalThis.document) {
    if (!doc?.querySelectorAll) return [];
    const ids = [];
    doc.querySelectorAll(CART_ELEMENT_SELECTORS).forEach((el) => {
      let entries = [];
      try { entries = el.getSectionsToRender?.() || []; } catch (_) { entries = []; }
      entries.forEach((entry) => {
        const id = entry?.section || entry?.id;
        if (id) ids.push(String(id));
      });
    });
    return uniq(ids);
  }

  function sectionIdsForCurrentUI(doc = globalThis.document, loc = globalThis.location) {
    const nativeIds = discoverNativeCartSectionIds(doc);
    const mounted = discoverMountedSectionIds(doc);
    const onCartPage = /\/cart\/?$/.test(loc?.pathname || '');
    const staticIds = onCartPage
      ? ['main-cart-items', 'main-cart-footer', 'cart-icon-bubble', 'cart-live-region-text']
      : ['cart-icon-bubble', 'cart-live-region-text'];
    return uniq([...nativeIds, ...mounted, ...staticIds, ...STATIC_SECTION_CANDIDATES]).slice(0, 5);
  }

  function requestPathWithSections(ids, loc = globalThis.location) {
    const url = new URL(loc?.href || shopPath('cart'), globalThis.location?.origin || 'https://example.invalid');
    url.searchParams.set('sections', ids.join(','));
    return `${url.pathname}${url.search}`;
  }

  function parseHTML(html) {
    if (!html || typeof DOMParser === 'undefined') return null;
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function sectionFragment(id, html) {
    const parsed = parseHTML(html);
    if (!parsed) return null;
    return parsed.getElementById?.(`shopify-section-${id}`) || parsed.body;
  }

  function replaceWrapperSection(id, html, doc = globalThis.document) {
    const current = doc?.getElementById?.(`shopify-section-${id}`);
    if (!current) return false;
    const parsed = parseHTML(html);
    const replacement = parsed?.getElementById?.(`shopify-section-${id}`) || parsed?.body?.firstElementChild;
    if (!replacement) return false;
    current.replaceWith(replacement);
    return true;
  }

  function replaceInner(selector, html, doc = globalThis.document, sectionId = null) {
    const current = doc?.querySelector?.(selector);
    if (!current) return false;
    const parsed = parseHTML(html);
    if (!parsed) return false;
    const replacement = parsed.querySelector(selector);
    if (replacement) {
      current.innerHTML = replacement.innerHTML;
      return true;
    }
    if (sectionId) {
      const fragment = parsed.getElementById?.(`shopify-section-${sectionId}`);
      if (fragment) {
        current.innerHTML = fragment.innerHTML;
        return true;
      }
    }
    return false;
  }

  function applySectionHTML(id, html, doc = globalThis.document) {
    if (!html) return false;
    if (replaceWrapperSection(id, html, doc)) return true;

    // Ride 15.5 notification mode mounts these section results inside the
    // header instead of as standalone shopify-section wrappers in the page DOM.
    if (id === 'cart-notification-product') {
      return replaceInner('#cart-notification-product', html, doc, id);
    }
    if (id === 'cart-notification-button') {
      return replaceInner('#cart-notification-button', html, doc, id);
    }
    if (id === 'cart-drawer') {
      return replaceInner('#CartDrawer', html, doc, id)
        || replaceInner('cart-drawer', html, doc, id)
        || replaceInner('[data-cart-drawer]', html, doc, id);
    }
    if (id === 'cart-icon-bubble') {
      return replaceInner('#cart-icon-bubble', html, doc, id)
        || replaceInner('[data-cart-icon-bubble]', html, doc, id);
    }
    if (id === 'cart-live-region-text') {
      return replaceInner('#cart-live-region-text', html, doc, id);
    }
    if (id === 'main-cart-items') return replaceInner('#main-cart-items', html, doc, id);
    if (id === 'main-cart-footer') return replaceInner('#main-cart-footer', html, doc, id);
    return false;
  }

  async function fetchCartSections({ ids, fetchImpl = globalThis.fetch, locationObj = globalThis.location } = {}) {
    const sectionIds = ids?.length ? uniq(ids).slice(0, 5) : sectionIdsForCurrentUI(globalThis.document, locationObj);
    if (!sectionIds.length || typeof fetchImpl !== 'function') return { ids: sectionIds, sections: {}, ok: false };
    const response = await fetchImpl(requestPathWithSections(sectionIds, locationObj), {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return { ids: sectionIds, sections: {}, ok: false, status: response.status };
    const sections = await response.json();
    return { ids: sectionIds, sections, ok: true, status: response.status };
  }

  async function refreshCartUI(options = {}) {
    try {
      const result = await fetchCartSections(options);
      if (!result.ok) return { ...result, applied: 0 };
      let applied = 0;
      Object.entries(result.sections || {}).forEach(([id, html]) => {
        if (applySectionHTML(id, html, options.documentObj || globalThis.document)) applied += 1;
      });
      return { ...result, applied };
    } catch (error) {
      console.warn('[TBP Calculator] cart section refresh failed', error);
      return { ids: [], sections: {}, ok: false, applied: 0, error };
    }
  }

  async function openCartUI({ cartUrl, cartType, documentObj = globalThis.document, locationObj = globalThis.location } = {}) {
    // Exact exported TBP Ride theme uses cart_type=notification. Prefer the
    // mounted native custom element after its sections were refreshed.
    if (cartType === 'notification') {
      const notification = documentObj?.querySelector?.('cart-notification');
      if (notification && typeof notification.open === 'function') {
        notification.open();
        return 'CART_NOTIFICATION_OPEN';
      }
    }

    if (cartType === 'drawer') {
      const drawer = documentObj?.querySelector?.('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return 'CART_DRAWER_OPEN';
      }
    }

    // Ride 15.5+ includes Shopify Standard Actions. Keep it as the next stable
    // compatibility path, but do not depend on it for notification mode.
    const action = globalThis.Shopify?.actions?.openCart;
    if (typeof action === 'function') {
      try {
        await action();
        return 'SHOPIFY_ACTION';
      } catch (error) {
        console.warn('[TBP Calculator] Shopify.actions.openCart failed, using compatibility fallback', error);
      }
    }

    const drawer = documentObj?.querySelector?.('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return 'CART_DRAWER_OPEN';
    }

    const notification = documentObj?.querySelector?.('cart-notification');
    if (notification && typeof notification.open === 'function') {
      notification.open();
      return 'CART_NOTIFICATION_OPEN';
    }

    const cartTrigger = documentObj?.querySelector?.('#cart-icon-bubble, [data-cart-icon-bubble], a[href$="/cart"]');
    if (cartTrigger && typeof cartTrigger.click === 'function') {
      cartTrigger.click();
      return 'CART_TRIGGER_CLICK';
    }

    const target = cartUrl || shopPath('cart');
    if (locationObj?.assign) locationObj.assign(target);
    return 'CART_PAGE_FALLBACK';
  }

  async function afterSuccessfulAdd({ cartUrl, cartType } = {}) {
    const refresh = await refreshCartUI();
    const openMode = await openCartUI({ cartUrl, cartType });
    return { refresh, openMode };
  }

  globalThis.TBPShopifyBridge = {
    version: '0.4.0',
    target: 'The Base Pace exported Ride 15.5.0 + Ride 16.x compatibility',
    getShopRoot,
    shopPath,
    discoverMountedSectionIds,
    discoverNativeCartSectionIds,
    sectionIdsForCurrentUI,
    requestPathWithSections,
    applySectionHTML,
    fetchCartSections,
    refreshCartUI,
    openCartUI,
    afterSuccessfulAdd
  };
})();
